// Assessment Results Integration Service - Handles assessment results storage and analysis
import AsyncStorage from '@react-native-async-storage/async-storage';

class AssessmentResultsIntegrationService {
  constructor() {
    this.isInitialized = false;
    this.assessmentResults = new Map();
    this.analytics = new Map();
    this.certificates = new Map();
  }

  // Initialize the service
  async initialize() {
    try {
      console.log('📊 Initializing Assessment Results Integration Service...');
      
      // Load cached data
      await this.loadCachedData();
      
      // Initialize analytics
      await this.initializeAnalytics();
      
      this.isInitialized = true;
      console.log('✅ Assessment Results Integration Service initialized successfully');
      
      return true;
    } catch (error) {
      console.error('❌ Failed to initialize Assessment Results Integration Service:', error);
      return false;
    }
  }

  // Load cached data
  async loadCachedData() {
    try {
      const keys = [
        'assessment_results',
        'assessment_analytics',
        'assessment_certificates'
      ];
      
      const cachedData = await AsyncStorage.multiGet(keys);
      
      cachedData.forEach(([key, value]) => {
        if (value) {
          const data = JSON.parse(value);
          switch (key) {
            case 'assessment_results':
              data.forEach(result => {
                this.assessmentResults.set(result.resultId, result);
              });
              break;
            case 'assessment_analytics':
              data.forEach(analytic => {
                this.analytics.set(analytic.candidateId, analytic);
              });
              break;
            case 'assessment_certificates':
              data.forEach(cert => {
                this.certificates.set(cert.certificateId, cert);
              });
              break;
          }
        }
      });
      
      console.log('📦 Cached assessment data loaded');
    } catch (error) {
      console.error('❌ Error loading cached data:', error);
    }
  }

  // Initialize analytics
  async initializeAnalytics() {
    // Setup periodic analytics updates
    setInterval(() => {
      this.updateAnalytics();
    }, 300000); // Update every 5 minutes
  }

  // Store assessment result
  async storeAssessmentResult(resultData) {
    try {
      const result = {
        resultId: resultData.resultId || `result_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        candidateId: resultData.candidateId,
        assessmentId: resultData.assessmentId,
        assessmentType: resultData.assessmentType || 'general',
        assessmentTitle: resultData.assessmentTitle,
        score: resultData.score,
        maxScore: resultData.maxScore || 100,
        percentage: resultData.percentage || Math.round((resultData.score / (resultData.maxScore || 100)) * 100),
        passed: resultData.passed !== undefined ? resultData.passed : (resultData.score >= 70),
        completedAt: resultData.completedAt || new Date().toISOString(),
        duration: resultData.duration || 0,
        questions: resultData.questions || [],
        answers: resultData.answers || [],
        feedback: resultData.feedback || [],
        recommendations: resultData.recommendations || [],
        skillsAssessed: resultData.skillsAssessed || [],
        certificateEligible: resultData.certificateEligible || (resultData.score >= 80),
        metadata: resultData.metadata || {}
      };

      this.assessmentResults.set(result.resultId, result);
      await this.saveAssessmentResults();

      // Update analytics
      await this.updateCandidateAnalytics(result.candidateId);

      // Generate certificate if eligible
      if (result.certificateEligible && result.passed) {
        await this.generateCertificate(result);
      }

      console.log('💾 Assessment result stored:', result.resultId);
      return result;
    } catch (error) {
      console.error('❌ Error storing assessment result:', error);
      throw error;
    }
  }

  // Store interview results
  async storeInterviewResults(interviewData) {
    try {
      const result = {
        resultId: `interview_${interviewData.interviewId}`,
        candidateId: interviewData.candidateId,
        assessmentId: interviewData.interviewId,
        assessmentType: 'interview',
        assessmentTitle: `Interview - ${interviewData.results.jobTitle || 'Position'}`,
        score: interviewData.results.overallScore,
        maxScore: 100,
        percentage: interviewData.results.overallScore,
        passed: interviewData.results.overallScore >= 70,
        completedAt: interviewData.completedAt,
        duration: interviewData.results.duration || 0,
        questions: interviewData.results.questions || [],
        answers: interviewData.results.responses || [],
        feedback: interviewData.results.feedback || [],
        recommendations: [interviewData.results.recommendation],
        skillsAssessed: interviewData.results.skillsAssessed || [],
        certificateEligible: false, // Interviews don't generate certificates
        metadata: {
          interviewType: 'ai_video',
          jobId: interviewData.jobId,
          categoryScores: interviewData.results.categoryScores,
          strengths: interviewData.results.strengths,
          improvements: interviewData.results.improvements
        }
      };

      return await this.storeAssessmentResult(result);
    } catch (error) {
      console.error('❌ Error storing interview results:', error);
      throw error;
    }
  }

  // Get assessment results by candidate
  getAssessmentResults(candidateId, filters = {}) {
    let results = Array.from(this.assessmentResults.values())
      .filter(result => result.candidateId === candidateId);

    // Apply filters
    if (filters.type) {
      results = results.filter(result => result.assessmentType === filters.type);
    }

    if (filters.passed !== undefined) {
      results = results.filter(result => result.passed === filters.passed);
    }

    if (filters.dateFrom) {
      results = results.filter(result => new Date(result.completedAt) >= new Date(filters.dateFrom));
    }

    if (filters.dateTo) {
      results = results.filter(result => new Date(result.completedAt) <= new Date(filters.dateTo));
    }

    if (filters.limit) {
      results = results.slice(0, filters.limit);
    }

    return results.sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt));
  }

  // Get assessment result by ID
  getAssessmentResult(resultId) {
    return this.assessmentResults.get(resultId);
  }

  // Get candidate analytics
  getCandidateAnalytics(candidateId) {
    return this.analytics.get(candidateId) || this.generateDefaultAnalytics(candidateId);
  }

  // Update candidate analytics
  async updateCandidateAnalytics(candidateId) {
    try {
      const results = this.getAssessmentResults(candidateId);
      
      if (results.length === 0) {
        return this.generateDefaultAnalytics(candidateId);
      }

      const analytics = {
        candidateId,
        totalAssessments: results.length,
        passedAssessments: results.filter(r => r.passed).length,
        failedAssessments: results.filter(r => !r.passed).length,
        averageScore: Math.round(results.reduce((sum, r) => sum + r.score, 0) / results.length),
        highestScore: Math.max(...results.map(r => r.score)),
        lowestScore: Math.min(...results.map(r => r.score)),
        totalTimeSpent: results.reduce((sum, r) => sum + (r.duration || 0), 0),
        lastAssessmentDate: results[0].completedAt,
        improvementTrend: this.calculateImprovementTrend(results),
        skillBreakdown: this.calculateSkillBreakdown(results),
        typeBreakdown: this.calculateTypeBreakdown(results),
        monthlyProgress: this.calculateMonthlyProgress(results),
        certificates: this.getCandidateCertificates(candidateId).length,
        updatedAt: new Date().toISOString()
      };

      this.analytics.set(candidateId, analytics);
      await this.saveAnalytics();

      return analytics;
    } catch (error) {
      console.error('❌ Error updating candidate analytics:', error);
      return this.generateDefaultAnalytics(candidateId);
    }
  }

  // Generate default analytics
  generateDefaultAnalytics(candidateId) {
    return {
      candidateId,
      totalAssessments: 0,
      passedAssessments: 0,
      failedAssessments: 0,
      averageScore: 0,
      highestScore: 0,
      lowestScore: 0,
      totalTimeSpent: 0,
      lastAssessmentDate: null,
      improvementTrend: 'stable',
      skillBreakdown: {},
      typeBreakdown: {},
      monthlyProgress: [],
      certificates: 0,
      updatedAt: new Date().toISOString()
    };
  }

  // Calculate improvement trend
  calculateImprovementTrend(results) {
    if (results.length < 2) return 'stable';

    const recent = results.slice(0, 3);
    const older = results.slice(3, 6);

    if (older.length === 0) return 'stable';

    const recentAvg = recent.reduce((sum, r) => sum + r.score, 0) / recent.length;
    const olderAvg = older.reduce((sum, r) => sum + r.score, 0) / older.length;

    const improvement = recentAvg - olderAvg;

    if (improvement > 5) return 'improving';
    if (improvement < -5) return 'declining';
    return 'stable';
  }

  // Calculate skill breakdown
  calculateSkillBreakdown(results) {
    const skillScores = {};

    results.forEach(result => {
      if (result.skillsAssessed && result.skillsAssessed.length > 0) {
        result.skillsAssessed.forEach(skill => {
          if (!skillScores[skill.name || skill]) {
            skillScores[skill.name || skill] = {
              scores: [],
              assessments: 0
            };
          }
          skillScores[skill.name || skill].scores.push(skill.score || result.score);
          skillScores[skill.name || skill].assessments++;
        });
      }
    });

    // Calculate averages
    Object.keys(skillScores).forEach(skill => {
      const data = skillScores[skill];
      data.averageScore = Math.round(data.scores.reduce((sum, score) => sum + score, 0) / data.scores.length);
      data.highestScore = Math.max(...data.scores);
      data.lowestScore = Math.min(...data.scores);
    });

    return skillScores;
  }

  // Calculate type breakdown
  calculateTypeBreakdown(results) {
    const typeBreakdown = {};

    results.forEach(result => {
      const type = result.assessmentType;
      if (!typeBreakdown[type]) {
        typeBreakdown[type] = {
          count: 0,
          totalScore: 0,
          passed: 0,
          failed: 0
        };
      }

      typeBreakdown[type].count++;
      typeBreakdown[type].totalScore += result.score;
      if (result.passed) {
        typeBreakdown[type].passed++;
      } else {
        typeBreakdown[type].failed++;
      }
    });

    // Calculate averages
    Object.keys(typeBreakdown).forEach(type => {
      const data = typeBreakdown[type];
      data.averageScore = Math.round(data.totalScore / data.count);
      data.passRate = Math.round((data.passed / data.count) * 100);
    });

    return typeBreakdown;
  }

  // Calculate monthly progress
  calculateMonthlyProgress(results) {
    const monthlyData = {};

    results.forEach(result => {
      const date = new Date(result.completedAt);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          month: monthKey,
          assessments: 0,
          totalScore: 0,
          passed: 0,
          failed: 0
        };
      }

      monthlyData[monthKey].assessments++;
      monthlyData[monthKey].totalScore += result.score;
      if (result.passed) {
        monthlyData[monthKey].passed++;
      } else {
        monthlyData[monthKey].failed++;
      }
    });

    // Calculate averages and sort by month
    return Object.values(monthlyData)
      .map(data => ({
        ...data,
        averageScore: Math.round(data.totalScore / data.assessments),
        passRate: Math.round((data.passed / data.assessments) * 100)
      }))
      .sort((a, b) => b.month.localeCompare(a.month));
  }

  // Generate certificate
  async generateCertificate(result) {
    try {
      const certificate = {
        certificateId: `cert_${result.resultId}`,
        candidateId: result.candidateId,
        resultId: result.resultId,
        assessmentTitle: result.assessmentTitle,
        assessmentType: result.assessmentType,
        score: result.score,
        percentage: result.percentage,
        issuedAt: new Date().toISOString(),
        validUntil: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(), // 1 year
        certificateNumber: this.generateCertificateNumber(),
        status: 'active',
        metadata: {
          issuer: 'JobEng Assessment Platform',
          verificationCode: this.generateVerificationCode()
        }
      };

      this.certificates.set(certificate.certificateId, certificate);
      await this.saveCertificates();

      console.log('🏆 Certificate generated:', certificate.certificateId);
      return certificate;
    } catch (error) {
      console.error('❌ Error generating certificate:', error);
      throw error;
    }
  }

  // Get candidate certificates
  getCandidateCertificates(candidateId) {
    return Array.from(this.certificates.values())
      .filter(cert => cert.candidateId === candidateId)
      .sort((a, b) => new Date(b.issuedAt) - new Date(a.issuedAt));
  }

  // Generate certificate number
  generateCertificateNumber() {
    const timestamp = Date.now().toString();
    const random = Math.random().toString(36).substr(2, 6).toUpperCase();
    return `JOBENG-${timestamp.slice(-8)}-${random}`;
  }

  // Generate verification code
  generateVerificationCode() {
    return Math.random().toString(36).substr(2, 12).toUpperCase();
  }

  // Get assessment statistics
  getAssessmentStatistics(candidateId) {
    const results = this.getAssessmentResults(candidateId);
    const analytics = this.getCandidateAnalytics(candidateId);

    return {
      overview: {
        totalAssessments: analytics.totalAssessments,
        passedAssessments: analytics.passedAssessments,
        failedAssessments: analytics.failedAssessments,
        averageScore: analytics.averageScore,
        passRate: analytics.totalAssessments > 0 ? Math.round((analytics.passedAssessments / analytics.totalAssessments) * 100) : 0
      },
      recent: results.slice(0, 5),
      trends: {
        improvementTrend: analytics.improvementTrend,
        monthlyProgress: analytics.monthlyProgress.slice(0, 6)
      },
      skills: analytics.skillBreakdown,
      types: analytics.typeBreakdown,
      certificates: this.getCandidateCertificates(candidateId).length
    };
  }

  // Save data methods
  async saveAssessmentResults() {
    try {
      const resultsArray = Array.from(this.assessmentResults.values());
      await AsyncStorage.setItem('assessment_results', JSON.stringify(resultsArray));
    } catch (error) {
      console.error('❌ Error saving assessment results:', error);
    }
  }

  async saveAnalytics() {
    try {
      const analyticsArray = Array.from(this.analytics.values());
      await AsyncStorage.setItem('assessment_analytics', JSON.stringify(analyticsArray));
    } catch (error) {
      console.error('❌ Error saving analytics:', error);
    }
  }

  async saveCertificates() {
    try {
      const certificatesArray = Array.from(this.certificates.values());
      await AsyncStorage.setItem('assessment_certificates', JSON.stringify(certificatesArray));
    } catch (error) {
      console.error('❌ Error saving certificates:', error);
    }
  }

  // Update analytics periodically
  async updateAnalytics() {
    try {
      const candidateIds = new Set();
      this.assessmentResults.forEach(result => {
        candidateIds.add(result.candidateId);
      });

      for (const candidateId of candidateIds) {
        await this.updateCandidateAnalytics(candidateId);
      }

      console.log('📊 Analytics updated for all candidates');
    } catch (error) {
      console.error('❌ Error updating analytics:', error);
    }
  }

  // Reset service
  reset() {
    this.assessmentResults.clear();
    this.analytics.clear();
    this.certificates.clear();
    this.isInitialized = false;
  }
}

export default AssessmentResultsIntegrationService;

